import { Button, Icons, Typography } from "@ster/ster-toolkit";
import { Alert } from "antd";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRecoilValue } from "recoil";

import {
  calculateState,
  campaignsForCalculateState,
  conversionGroupsState,
  metadataState,
} from "./atoms";
import { Campaign } from "./Compare";
import ResultItem from "./ResultItem";
import styles from "./Results.module.less";

// Bepaal of een element zichtbaar is
const useOnScreen = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting)
      ),
    []
  );

  useEffect(() => {
    if (ref.current != null) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [observer, ref]);

  return isIntersecting;
};

const Results = ({
  campaigns,
  medium,
}: {
  campaigns: Partial<Campaign>[];
  medium: string;
}): JSX.Element | null => {
  const metadata = useRecoilValue(metadataState(medium));
  const [showScrollDown, setShowScrollDown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);

  const results = useRecoilValue(
    calculateState({
      campaigns: useRecoilValue(campaignsForCalculateState),
      conversionGroups: useRecoilValue(conversionGroupsState)?.map(
        (s) => s.value
      ),
      medium,
    })
  );

  useEffect(() => {
    if (results) {
      setShowScrollDown(true);
      setTimeout(() => setShowScrollDown(false), 5000);
    }
  }, [results]);

  useEffect(() => {
    if (isVisible) {
      setShowScrollDown(false);
    }
  }, [isVisible]);

  const scrollToResults = useCallback(() => {
    document.getElementById("results")?.scrollIntoView({ block: "end" });
  }, []);

  if (!results) {
    return null;
  }

  return (
    <div className={styles.results} id="results">
      {showScrollDown && (
        <Button
          className={styles.scrollDown}
          onClick={scrollToResults}
          mode="secondary"
        >
          <Typography.Title level={4}>Resultaten</Typography.Title>
          <Icons.ChevronDownIcon />
        </Button>
      )}
      <div className={styles.conversionGroups}>
        <div
          className={classNames({
            [styles.container]: (results.conversionGroups?.length ?? 0) > 0,
          })}
        >
          {results.conversionGroups?.map((r) => (
            <ResultItem
              result={r}
              campaigns={campaigns}
              key={JSON.stringify(r)}
              metadata={metadata}
            />
          ))}
        </div>
      </div>
      <div
        ref={ref}
        className={classNames({
          [styles.container]: (results.targetGroups?.length ?? 0) > 0,
        })}
      >
        {results.targetGroups?.map((r) => (
          <ResultItem
            result={r}
            campaigns={campaigns}
            key={r.targetGroupName}
            metadata={metadata}
          />
        ))}
      </div>
      {!results.targetGroups &&
        (campaigns.some(
          (s) =>
            s.operator === "adalliance" &&
            JSON.stringify(s.channels) === JSON.stringify(["overige zenders"])
        ) ? (
          <Alert
            showIcon
            className={styles.warning}
            type="warning"
            message=""
            description="Het is op dit moment niet mogelijk om alleen voor de ‘overige zenders’ van Ad Alliance een prognose te maken. "
          />
        ) : (
          <Alert
            showIcon
            className={styles.warning}
            type="warning"
            message=""
            description="Het is op dit moment niet mogelijk om voor deze campagne(s) een prognose te maken."
          />
        ))}
    </div>
  );
};

export default Results;
